import { AccountType } from '@lune-climate/lune'
import { LoadingWrapper, LuneThemeNew, Switch, Text, Tooltip } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { FC, memo, useCallback, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'

import DiscardChangesConfirmation from 'components/DiscardChangesConfirmation'
import { ACCOUNT_ID_QUERY_PARAM_KEY } from 'components/FiltersWithQueryParamsWrapper'
import { setUserPrimaryAccountId } from 'endpoints/dapi'
import useHasUnsavedChanges from 'hooks/useHasUnsavedChanges'
import { useLuneClient } from 'hooks/useLuneClient'
import useMixpanel from 'hooks/useMixpanel'
import { queryKeys } from 'queryKeys'

const getActiveAndSiblingAccounts = (luneClient: ReturnType<typeof useLuneClient>) =>
    luneClient.getAccount().then((r) => {
        const activeAccount = r.unwrap()
        const siblingId = activeAccount.siblingId ?? null
        return {
            liveId: activeAccount.type === AccountType.LIVE ? activeAccount.id : siblingId,
            testId: activeAccount.type === AccountType.LIVE ? siblingId : activeAccount.id,
            currenType: activeAccount.type,
            hasSibling: siblingId !== null,
        }
    })

/**
 * TestModeSwitch needs its own, unique Query which will remain static while
 * we invalidate all other queries. This prevents re-renders and allows the Switch animation
 * to play.
 */
const TestModeSwitch: FC = () => {
    const navigate = useNavigate()
    const { palette } = LuneThemeNew
    const [isTestMode, setIsTestMode] = useState(false)
    const [hasSibling, setHasSibling] = useState(true)
    const [searchParams, setSearchParams] = useSearchParams()

    const { hasUnsavedChanges, setHasUnsavedChangesState } = useHasUnsavedChanges()
    const [loading, setLoading] = useState(true)
    const [openModal, setOpenModal] = useState<boolean>(false)
    const [checked, setChecked] = useState<boolean>(isTestMode)
    const luneClient = useLuneClient()
    const mixpanel = useMixpanel()

    useEffect(() => {
        async function getAccountData() {
            const account: {
                liveId: string | null
                testId: string | null
                currenType: AccountType
                hasSibling: boolean
            } = await getActiveAndSiblingAccounts(luneClient)
            setIsTestMode(account.currenType === AccountType.TEST)
            setChecked(account.currenType === AccountType.TEST)
            setHasSibling(account.hasSibling)
            setLoading(false)
        }
        getAccountData()
    }, [luneClient])

    const { data: activeAndSibling } = useQuery<{
        liveId: string | null
        testId: string | null
        currenType: AccountType
        hasSibling: boolean
    }>(queryKeys.GET_ACTIVE_ACCOUNT_FOR_TEST_SWITCH, () => getActiveAndSiblingAccounts(luneClient))
    const queryClient = useQueryClient()

    const animateToggle = (checked: boolean) => {
        setTimeout(() => {
            setChecked(checked)
        }, 100)
    }

    const onSwitch = useCallback(
        (checked: boolean) => {
            setIsTestMode(!isTestMode)

            const newPrimaryAccountId = checked
                ? activeAndSibling?.testId
                : activeAndSibling?.liveId

            if (!newPrimaryAccountId) return

            setUserPrimaryAccountId(newPrimaryAccountId).then(async () => {
                mixpanel.track('AccountSwitched', {
                    id: newPrimaryAccountId,
                })

                const path = window.location.pathname

                // Special case for TEST MODE Switch on specific, individual page
                if (
                    path.includes(`/settings/accounts/`) &&
                    !path.includes(`/settings/accounts/new`)
                ) {
                    const accountId = path.replace(`/settings/accounts/`, '')
                    const acc = (await luneClient.getAccount({ accountId })).unwrap()
                    if (acc.siblingId) {
                        navigate(`/settings/accounts/${acc.siblingId}`, { replace: true })
                    } else {
                        navigate(`/settings/accounts`, { replace: true })
                    }
                    setHasUnsavedChangesState(false)
                }

                // Special case for TEST MODE Switch on specific, individual page
                if (path.includes(`/client-accounts/`)) {
                    navigate(`/client-accounts`, { replace: true })
                }

                // Special case for TEST MODE Switch on Order details page (e.g. /orders/rKqngNM0G)
                if (path.includes(`/orders/`)) {
                    // Navigate to /orders because the current order details now belong to an order of the "opposite" mode
                    navigate(`/orders`, { replace: true })
                }

                // Special case for TEST MODE Switch on Orders list page (e.g. /orders or /orders?account_id=q9aKx7b etc)
                if (path.match(/^\/orders(\?.*)?$/)) {
                    // Switching between test/live mode means switching between sets of accounts
                    // Because OrdersList account filters are stored in the URL as query param account_id,
                    // we need to remove that query param when switching between modes to avoid showing stale data.
                    if (searchParams.has(ACCOUNT_ID_QUERY_PARAM_KEY)) {
                        searchParams.delete(ACCOUNT_ID_QUERY_PARAM_KEY)
                        setSearchParams(searchParams)
                    }
                }

                await queryClient.invalidateQueries({
                    predicate: (query) => {
                        const keysToExclude = [
                            queryKeys.GET_ACTIVE_ACCOUNT_FOR_TEST_SWITCH,
                            // Excluding RECENTLY_USED_ACCOUNTS because refetching is handled in useRecentltUsedAccounts hook manually
                            queryKeys.RECENTLY_USED_ACCOUNTS,
                        ]

                        // Check if the query key is not in the keysToExclude array
                        return !keysToExclude.includes(query.queryKey as queryKeys)
                    },
                })
            })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeAndSibling, mixpanel],
    )

    const toggle = (switchValue: any) => {
        const path = window.location.pathname
        const shouldShowConfirmationModal =
            hasUnsavedChanges &&
            !path.includes(`/settings/accounts/new`) &&
            !path.includes(`/order`) &&
            !path.includes(`/proposal-request`)
        if (shouldShowConfirmationModal) {
            setOpenModal(true)
        } else {
            animateToggle(switchValue.target.checked)
            onSwitch(switchValue.target.checked)
        }
    }

    return (
        <>
            <Box
                sx={{
                    flex: '1 1 auto',
                    backgroundColor: isTestMode ? palette.Yellow200 : `initial`,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pl: 3,
                        pr: 3,
                        '> span:first-of-type': {
                            display: 'inline-block',
                            width: '100%',
                        },
                    }}
                >
                    <LoadingWrapper loading={loading}>
                        <Tooltip
                            sx={{ left: '16px !important' }}
                            title={
                                <Box
                                    sx={{
                                        maxWidth: '228px',
                                    }}
                                >
                                    {hasSibling ? (
                                        <>
                                            Use Test mode to place orders without being charged and
                                            create Test API keys.
                                            <br />
                                            <br />
                                            Toggle off when you&apos;re ready to go live.
                                        </>
                                    ) : (
                                        'Test mode is not available for this account.'
                                    )}
                                </Box>
                            }
                            placement="top"
                        >
                            <Text
                                variant={`body3`}
                                sx={{
                                    width: '100%',
                                    height: '66px',
                                    lineHeight: '66px',
                                }}
                            >
                                Test mode
                            </Text>
                        </Tooltip>
                        <Switch
                            data-testid={'test-mode-switch'}
                            defaultChecked={checked}
                            onChange={toggle}
                            disabled={!hasSibling}
                        />
                    </LoadingWrapper>
                </Box>
            </Box>
            {openModal && (
                <DiscardChangesConfirmation
                    onConfirm={() => {
                        setOpenModal(false)
                        setHasUnsavedChangesState(false)
                        onSwitch(!checked)
                        animateToggle(!checked)
                    }}
                    onCancel={() => {
                        setOpenModal(false)
                        animateToggle(checked)
                    }}
                />
            )}
        </>
    )
}

export default memo(TestModeSwitch)
